import { inject, Injectable } from '@angular/core';
import { AccountGroup, AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import type { GetPartnerModelResponse } from '@vendasta/protos/partner/v1/types';
import { getPartnerModel$ } from '@vendasta/protos/partner/v1/partner-model-service';
import { OwnerType } from '@vendasta/smsv2';
import { catchError, map, Observable, of, switchMap } from 'rxjs';

import { SmsInjectionToken } from '../token';
import { OwnerLocation } from '../registration/types/registration-types';

@Injectable({
  providedIn: 'root',
})
export class OwnerService {
  private readonly config = inject(SmsInjectionToken);
  private readonly accountGroupService = inject(AccountGroupService);

  private readonly ownerId$ = this.config.ownerId$;
  private readonly ownerType: OwnerType = this.config.ownerType;

  getOwnerLocation$(): Observable<OwnerLocation> {
    if (this.ownerType === OwnerType.OWNER_TYPE_ACCOUNT_GROUP) {
      return this.getAccountGroupInfo$().pipe(
        map((accountGroupInfo) => {
          return {
            ownerId: accountGroupInfo?.accountGroupId,
            ownerType: OwnerType.OWNER_TYPE_ACCOUNT_GROUP,
            streetAddress: accountGroupInfo?.napData?.address,
            streetAddressSecondary: accountGroupInfo?.napData?.address2,
            city: accountGroupInfo?.napData?.city,
            state: accountGroupInfo?.napData?.state,
            postalCode: accountGroupInfo?.napData?.zip,
            country: accountGroupInfo?.napData?.country,
          };
        }),
      );
    } else if (this.ownerType === OwnerType.OWNER_TYPE_PARTNER) {
      return this.ownerId$.pipe(
        switchMap((partnerId) => getPartnerModel$({ partnerId })),
        map((partnerResponse: GetPartnerModelResponse) => {
          const partnerModel = partnerResponse?.partner;
          return {
            ownerId: partnerModel?.partnerId,
            ownerType: OwnerType.OWNER_TYPE_PARTNER,
            streetAddress: partnerModel?.addressLineOne,
            streetAddressSecondary: partnerModel?.addressLineTwo,
            city: partnerModel?.city,
            state: partnerModel?.stateCode,
            postalCode: partnerModel?.postalCode,
            country: partnerModel?.countryCode,
          };
        }),
      );
    }
    return of({} as OwnerLocation);
  }

  getAccountGroupInfo$(): Observable<AccountGroup> {
    return this.ownerId$.pipe(
      switchMap((ownerId) => {
        return this.accountGroupService
          .get(ownerId, new ProjectionFilter({ napData: true }))
          .pipe(catchError(() => of({} as AccountGroup)));
      }),
    );
  }
}
